import { IPosition, IPositionType } from 'src/generated-types'
import { ComponentCount } from 'src/generated-types/component-count'
import { IChangeType, IComponentChange, IPositionChange } from 'src/generated-types/project-version-changes'

// A revision is priced on what it changes only: new and replaced parts at the new product's price, extra quantity
// on an unchanged part, and added work. Removed parts give no credit.
const pricedQuantity = (component: IComponentChange) => {
  if (component.type === IChangeType.Removed) return 0
  if (component.type === IChangeType.Added) return component.quantityAfter
  const replaced = component.previousProductNumber != null || component.previousDescription != null
  return replaced ? component.quantityAfter : Math.max(0, component.quantityAfter - component.quantityBefore)
}

export const componentCost = (component: IComponentChange) =>
  pricedQuantity(component) * (component.unitPriceAfter ?? 0)

const workCost = (change: IPositionChange) =>
  change.work.filter(w => w.type === IChangeType.Added).reduce((sum, w) => sum + w.quantity * (w.unitPrice ?? 0), 0)

export const positionCost = (change: IPositionChange) => {
  const products = change.components.reduce((sum, c) => sum + componentCost(c), 0)
  const work = workCost(change)
  return { products, work, total: products + work }
}

export const revisionTotals = (changes: IPositionChange[]) =>
  changes.reduce(
    (sum, change) => {
      const cost = positionCost(change)
      return { products: sum.products + cost.products, work: sum.work + cost.work, total: sum.total + cost.total }
    },
    { products: 0, work: 0, total: 0 },
  )

// Priced parts across all changed positions as rows of the project's products summary table:
// one per product, grouped by category, most expensive category first.
export const productSummaryRows = (changes: IPositionChange[], noCategoryLabel: string): ComponentCount[] => {
  const rows = new Map<string, ComponentCount>()
  changes.forEach(change =>
    change.components.forEach(component => {
      const quantity = pricedQuantity(component)
      if (quantity <= 0) return
      const key = component.productNumber ?? component.description ?? ''
      const row = rows.get(key) ?? {
        productNumber: component.productNumber ?? '',
        productDescription: component.description ?? '',
        quantity: 0,
        unitPrice: component.unitPriceAfter ?? 0,
        productCategoryId: 0,
        productCategoryName: component.category ?? noCategoryLabel,
      }
      row.quantity += quantity
      rows.set(key, row)
    }),
  )

  const sum = (row: ComponentCount) => row.quantity * row.unitPrice
  const categorySums = new Map<string, number>()
  rows.forEach(row =>
    categorySums.set(row.productCategoryName, (categorySums.get(row.productCategoryName) ?? 0) + sum(row)),
  )
  // The table groups rows by category id, in the order the categories first appear.
  const categoryOrder = [...categorySums].sort((a, b) => b[1] - a[1]).map(([name]) => name)
  return [...rows.values()]
    .map(row => ({ ...row, productCategoryId: categoryOrder.indexOf(row.productCategoryName) + 1 }))
    .sort((a, b) => a.productCategoryId - b.productCategoryId || sum(b) - sum(a))
}

export interface IWorkLine {
  key: string
  position: string
  description?: string
  quantity: number
  unitPrice?: number
  amount: number
}

export const workLines = (changes: IPositionChange[]): IWorkLine[] =>
  changes.flatMap(change =>
    change.work
      .filter(w => w.type === IChangeType.Added)
      .map((w, index) => ({
        key: `${change.name}-${index}`,
        position: change.name,
        description: w.description ?? w.productNumber,
        quantity: w.quantity,
        unitPrice: w.unitPrice,
        amount: w.quantity * (w.unitPrice ?? 0),
      })),
  )

export interface ITypeSummary {
  type: IPositionType
  total: number
  changes: IPositionChange[]
}

const TYPE_ORDER = [
  IPositionType.MooringLine,
  IPositionType.FrameLines,
  IPositionType.Bridle,
  IPositionType.Corner,
  IPositionType.Cage,
]

// Changes per position type; a type's total counts every position of the draft and of the published version once.
export const summariseByType = (
  changes: IPositionChange[],
  positions: IPosition[],
  basePositions: IPosition[],
): ITypeSummary[] => {
  const typeByName = new Map([...basePositions, ...positions].map(p => [p.name ?? '', p.type]))
  const namesByType = new Map<IPositionType, Set<string>>()
  typeByName.forEach((type, name) => namesByType.set(type, (namesByType.get(type) ?? new Set()).add(name)))
  const types = [...namesByType.keys()].sort(
    (a, b) => (TYPE_ORDER.indexOf(a) + 1 || 99) - (TYPE_ORDER.indexOf(b) + 1 || 99),
  )
  return types.map(type => ({
    type,
    total: namesByType.get(type)?.size ?? 0,
    changes: changes.filter(change => typeByName.get(change.name) === type),
  }))
}

export const formatKr = (value: number, decimals = 0) =>
  `${value.toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} kr`
